import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { TextStyles } from '@payler/ui-theme';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { TPaymentFiltersForm } from './types';
import { Templates } from './Templates';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import createLogger from 'debug';
import { useClosePaymentsFiltersModal } from '../../modals/PaymentsFiltersModal';
import {
  createDateFieldResolver,
  FloatingInputField,
  ThinDivider,
} from '@payler/ui-components';
import { useURLContractCurrencies } from '../../hooks/use-url-contract-currencies';
import { FiltersOptionsArrayAccordion } from '../../components/FiltersOptionsArrayAccordion/FiltersOptionsArrayAccordion';
import { FiltersDateRangeAccordion } from '../../components/FiltersDateRangeAccordion/FiltersDateRangeAccordion';
import {
  PAYMENT_FILTERS_DEBIT_STATUSES,
  PAYMENT_FILTERS_PAYMENT_METHODS,
} from '../../const';
import { FiltersAmountRangeAccordion } from '../../components/FiltersAmountRangeAccordion/FiltersAmountRangeAccordion';
import { FiltersInputAccordion } from '../../components/FiltersInputAccordion/FiltersInputAccordion';
import {
  usePaymentsFiltersFromUrl,
  useSetPaymentFiltersUrlSearchParams,
} from '../../hooks/payments/url';
import { useSavePaymentsTemplate } from '../../hooks/payments/templates';
import isEqual from 'lodash/isEqual';
import { isNil, omitBy } from 'lodash';
import { useResetFormAndUrlFilters } from '../../hooks/use-reset-form-and-url-filters';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useUIDateFormat } from '../../hooks/use-ui-dateFormat';
import { useSearchParams } from 'react-router-dom';
import { createPeriodRangeValidator } from '../../helpers/period-validation';
import { usePaymentIndicatorsByContract } from '../../hooks/contracts';
import { useMerchantCabinetConfig } from '../../config/ConfigProvider';
import { useDateRangeLogic } from '../../hooks/use-date-range-logic';

const log = createLogger('PaymentsFilters');

const getDefaultValues = (): TPaymentFiltersForm => {
  const dateFormat = 'DD.MM.YYYY';
  return {
    OrderId: '',
    StartAmount: null,
    EndAmount: null,
    StartDate: null,
    EndDate: dayjs().format(dateFormat),
    TransactionStatuses: [],
    PaymentMethods: [],
    PaymentIndicators: [],
    CurrencyCodes: [],
    OperationStatuses: [],
    IsAntifraudDeclined: null,
    Countries: null,
    CardHolder: null,
    CardLast4: null,
    Userdata: null,
    RRN: null,
  };
};

const useCreateResolver = () => {
  const { t } = useTranslation();
  const dateFormat = useUIDateFormat();
  const { environment } = useMerchantCabinetConfig();
  const isProdEnv = environment === 'prod';
  return useCallback(() => {
    return yupResolver(
      yup.object({
        StartDate: yup
          .string()
          .nullable()
          .test(
            'required',
            t('common:datePicker.required', 'Field cannot be empty'),
            (value) => {
              if (!isProdEnv) return true;
              const isEmpty = !value || value === '' || String(value).trim() === '';
              return !isEmpty;
            },
          )
          .concat(
            createDateFieldResolver(t, {
          toFieldName: 'EndDate',
          maxDate: dayjs(),
          dateFormat,
              canBeEmpty: !isProdEnv,
            }),
          )
          .test(
          'periodRange',
          t('common:datePicker.rangeError', 'The period cannot exceed 1 month'),
            createPeriodRangeValidator(t, dateFormat, 'EndDate', environment, true),
        ),
        EndDate: yup
          .string()
          .nullable()
          .test(
            'required',
            t('common:datePicker.required', 'Field cannot be empty'),
            (value) => {
              if (!isProdEnv) return true;
              const isEmpty = !value || value === '' || String(value).trim() === '';
              return !isEmpty;
            },
          )
          .concat(
            createDateFieldResolver(t, {
              fromFieldName: 'StartDate',
              maxDate: dayjs(),
              dateFormat,
              canBeEmpty: !isProdEnv,
            }),
          )
          .test(
            'periodRange',
            t('common:datePicker.rangeError', 'The period cannot exceed 1 month'),
            createPeriodRangeValidator(t, dateFormat, 'StartDate', environment, true),
          ),
      }),
    );
  }, [dateFormat, t, environment, isProdEnv]);
};

export const PaymentsFiltersForm = () => {
  const closeModal = useClosePaymentsFiltersModal();
  const setUrlFilters = useSetPaymentFiltersUrlSearchParams();
  const filters = usePaymentsFiltersFromUrl();
  const createResolver = useCreateResolver();
  const { t } = useTranslation(['fields']);
  const [searchParams, setSearchParams] = useSearchParams();
  const methods = useForm<TPaymentFiltersForm>({
    defaultValues: getDefaultValues(),
    resolver: createResolver(),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const { reset } = methods;

  useEffect(() => {
    const endDateInUrl = searchParams.get('EndDate');
    if (!endDateInUrl) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('EndDate', dayjs().format('DD.MM.YYYY'));
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    log('updating filters from url: %O', filters);
    reset(filters);
  }, [reset, filters]);

  useDateRangeLogic('StartDate', 'EndDate', methods);

  const handleSubmit = methods.handleSubmit((values) => {
    log('applying filters: %O', values);
    setUrlFilters(values);
    closeModal();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <VStack alignItems="stretch" spacing={0} divider={<ThinDivider />}>
          <Header />
          <React.Suspense fallback={null}>
            <Templates />
          </React.Suspense>
          <Box p={2}>
            <FloatingInputField
              name="OrderId"
              label={t('fields:transactionNumber')}
            />
          </Box>
          <DateRange />
          <TransactionStatus />
          <PaymentIndicators />
          <PaymentMethods />
          <React.Suspense fallback={null}>
            <Currencies />
          </React.Suspense>
          <Amount />
          <CardLast4 />
          <RRN />
          <Buttons />
        </VStack>
      </form>
    </FormProvider>
  );
};

const arrayAndNotEmpty = (val: unknown) => Array.isArray(val) && val.length > 0;

const useFiltersCount = () => {
  const { watch } = useFormContext<TPaymentFiltersForm>();
  const data = watch();
  return useMemo(() => {
    let count = 0;
    count += data.StartAmount || data.EndAmount ? 1 : 0;
    count += data.StartDate || data.EndDate ? 1 : 0;
    count += arrayAndNotEmpty(data.PaymentMethods) ? 1 : 0;
    count += arrayAndNotEmpty(data.PaymentIndicators) ? 1 : 0;
    count += arrayAndNotEmpty(data.TransactionStatuses) ? 1 : 0;
    count += arrayAndNotEmpty(data.OperationStatuses) ? 1 : 0;
    count += arrayAndNotEmpty(data.CurrencyCodes) ? 1 : 0;
    count += data.CardLast4 ? 1 : 0;
    count += data.RRN ? 1 : 0;
    return count;
  }, [data]);
};

const Header = () => {
  const { t } = useTranslation(['filters']);
  const reset = useResetFormAndUrlFilters(true);
  const count = useFiltersCount();

  return (
    <HStack justifyContent="space-between">
      <Box pl={3} pr={2} py={2} alignSelf="flex-start">
        <Text as="h4" textStyle={TextStyles.h4} color="primary.500">
          {t('filters:title')}
        </Text>
        <Text textStyle={TextStyles.Subtitle14Regular} color="primary.400">
          {count} {t('filters:selected')}
        </Text>
      </Box>
      <Box pr={3}>
        <Button variant="secondary" onClick={reset}>
          {t('filters:reset')}
        </Button>
      </Box>
    </HStack>
  );
};

const isEmpty = (val: unknown) =>
  isNil(val) || (Array.isArray(val) && val.length === 0);

const Buttons = () => {
  const filters = usePaymentsFiltersFromUrl();
  const count = useFiltersCount();
  const { t } = useTranslation(['filters']);
  const { getValues, watch } = useFormContext<TPaymentFiltersForm>();
  const saveMutation = useSavePaymentsTemplate();
  const handleSave = () => {
    saveMutation.mutate(getValues());
  };

  const applyDisabled = isEqual(
    omitBy(filters, isEmpty),
    omitBy(watch(), isEmpty),
  );

  return (
    <VStack p={2} alignItems="center" spacing="10px">
      <Button type="submit" w="100%" disabled={applyDisabled}>
        {t('filters:submit')}
      </Button>
      <Button
        variant="link"
        disabled={!count || saveMutation.status === 'pending'}
        onClick={handleSave}
      >
        {t('filters:save')}
      </Button>
    </VStack>
  );
};

export default PaymentsFiltersForm;

//region fields
export const Currencies = () => {
  const currencies = useURLContractCurrencies();
  return (
    <FiltersOptionsArrayAccordion
      fieldNameKey="fields:currency"
      formFieldName="CurrencyCodes"
      options={currencies}
      optionsPrefix="currency:"
      multi
    />
  );
};

export const DateRange = () => {
  const dateFormat = useUIDateFormat();
  return (
    <FiltersDateRangeAccordion
      formFieldFrom="StartDate"
      formFieldTo="EndDate"
      fromKey="fields:from"
      titleKey="fields:period"
      toKey="fields:to"
      dateFormat={dateFormat}
    />
  );
};

export const PaymentIndicators = () => {
  const paymentIndicators = usePaymentIndicatorsByContract();

  return (
    <FiltersOptionsArrayAccordion
      fieldNameKey="fields:paymentIndicator"
      formFieldName="PaymentIndicators"
      options={paymentIndicators}
      optionsPrefix="paymentIndicator:"
      multi
    />
  );
};

export const PaymentMethods = () => {
  return (
    <FiltersOptionsArrayAccordion
      fieldNameKey="fields:paymentMethod"
      formFieldName="PaymentMethods"
      options={PAYMENT_FILTERS_PAYMENT_METHODS}
      optionsPrefix="paymentMethod:"
      multi
    />
  );
};

export const Amount = () => (
  <FiltersAmountRangeAccordion
    formFieldFrom="StartAmount"
    formFieldTo="EndAmount"
    fromKey="fields:from"
    toKey="fields:to"
    titleKey="fields:amount"
  />
);

export const TransactionStatus = () => (
  <FiltersOptionsArrayAccordion
    fieldNameKey="fields:paymentStatus"
    formFieldName="TransactionStatuses"
    options={PAYMENT_FILTERS_DEBIT_STATUSES}
    optionsPrefix="transactionStatus:"
    multi
  />
);


export const CardLast4 = () => {
  const { t } = useTranslation(['fields']);
  return (
    <FiltersInputAccordion
      fieldNameKey="cardLast4"
      formFieldName="CardLast4"
      placeholder={t('fields:cardLast4Placeholder')}
      inputType="number"
      maxLength={4}
    />
  );
};

export const RRN = () => {
  const { t } = useTranslation(['fields']);
  return (
    <FiltersInputAccordion
      fieldNameKey="rrn"
      formFieldName="RRN"
      placeholder={t('fields:rrnPlaceholder')}
      inputType="text"
    />
  );
};
//endregion
